import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchSession, sessionQueryKey, hasModule } from "@/lib/auth";
import { branchKeys, listBranches } from "@/features/branches/branches.api";
import { formatTripDate, todayIso } from "@/features/trips/tripLabels";
import { getPlanningDay, getPlanningMap, getPlanningVehicles, NEXT_RADIUS_KM, planningKeys, type CapacityBoardData } from "@/features/planning/planning.api";
import { withTripNow } from "@/features/planning/capacityV1";
import { DayCost, StepBar } from "@/features/planning/PlanScreen";
import { PlanMapPane } from "@/features/planning/PlanMapPane";
import { MapWithTripSheet } from "@/features/planning/MapTripSheet";
import { CapacityWindow } from "@/features/planning/CapacityWindow";
import type { StepOneState } from "@/features/planning/PlanStep1";
import type { StepTwoState } from "@/features/planning/PlanStep2";
import type { StepThreeState } from "@/features/planning/PlanStep3";
import type { PlanStep } from "@/features/planning/planSteps";
import { usePlanningChannel, type CapDrag, type CapFocus } from "@/features/planning/planningChannel";
import { rememberMapWindowPlace } from "@/features/planning/mapWindow";
import { TrackingMapView, type TrackingChoice } from "@/features/tracking/TrackingMapView";
import type { TrackStage } from "@/features/tracking/tracking";

// The planning map in a window of its own, for a second screen (Camiel, 22-09-2026: "dan heb
// je 2 schermen"). Outside the _shell layout, as the mail window is: a window with the menu
// bars is a second copy of the application, not a map.
//
// The address holds all it shows — the day and the branch — so a reload keeps it. The board
// steers it over the planning channel (planningChannel.ts): another day or branch, the step of
// Plannen and what it shows. Without a board it still works, on step 1, and reads the day again
// every minute. Ritopvolging borrows the window for its own map (P60). In step 4 the window holds
// no map but the board of capacity, with the drivers and trailers beside it (Capaciteit v1, P107;
// CapacityWindow.tsx): there is no map with capacity.
//
// Until 27-10-2026 the window also held the map and the table of what waits for the Dagbord and the
// worksheet of the board (LegsTable.tsx, 23-09-2026); those tabs were off the screen since 19-10-2026.

export const Route = createFileRoute("/planning-map")({
  // The shell's guard: a second window shares the session cookie, so this passes without a
  // round trip. The module check is in the component, not a redirect — every route this app
  // would send you to lives under the shell (see mail-window.tsx).
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session };
  },
  validateSearch: (search: Record<string, unknown>): { date?: string; branch?: string } => ({
    date: typeof search["date"] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search["date"]) ? search["date"] : undefined,
    branch: typeof search["branch"] === "string" && search["branch"] ? search["branch"] : undefined,
  }),
  component: PlanningMapWindow,
});

function PlanningMapWindow() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const canSee = hasModule(session, "tms_planning") || hasModule(session, "tms_planning_write");
  const canPlan = session.roles.includes("admin") || hasModule(session, "tms_planning_write");
  const date = search.date ?? todayIso();
  const branch = search.branch ?? "";
  // The step of Plannen the board shows: the same step here.
  const [planStep, setPlanStep] = useState<PlanStep>(1);
  // What step 1 on the board shows: the full deliveries or the open ends, what is chosen there, how far it looks, and the
  // open ends ticked (P83).
  const [boardPlan, setBoardPlan] = useState<StepOneState | null>(null);
  // What step 2 on the board shows: the selection, what is ticked, the trip being built; and L from the board.
  const [boardPlan2, setBoardPlan2] = useState<StepTwoState | null>(null);
  // What step 3 on the board shows: the trip in focus in the overview.
  const [boardPlan3, setBoardPlan3] = useState<StepThreeState | null>(null);
  // Ritopvolging has this window (P60): the lorry chosen there and which trips it shows; null while the board has it.
  const [tracking, setTracking] = useState<{ choice: TrackingChoice | null; stage: TrackStage } | null>(null);
  const [armTick, setArmTick] = useState(0);
  // Step 4: the trip chosen and the list with the focus on the board, and what the board drags right now.
  const [cap, setCap] = useState<{ tripId: string | null; focus: CapFocus; tick: number } | null>(null);
  const [capDrag, setCapDrag] = useState<CapDrag | null>(null);

  const post = usePlanningChannel((message) => {
    switch (message.t) {
      case "tracking":
        setTracking({ choice: message.vehicleId ? { vehicleId: message.vehicleId, tripId: message.tripId } : null, stage: message.stage });
        break;
      case "trackingOff":
        setTracking(null);
        break;
      case "view":
        // The board speaks: the window is the planning's again (P60).
        setTracking(null);
        // The board went to another day or branch: the window goes along, in its address too.
        if (message.date !== date || message.branch !== branch) {
          void navigate({
            search: { date: message.date, branch: message.branch || undefined },
            replace: true,
          });
        }
        break;
      case "changed":
        void queryClient.invalidateQueries({ queryKey: ["planning", "map"] });
        void queryClient.invalidateQueries({ queryKey: ["planning", "day"] });
        void queryClient.invalidateQueries({ queryKey: ["planning", "crew"] });
        break;
      case "capShow":
        // Delete, the cross or Ctrl Z on the main screen, on its way to the API: on the board at once (punt 17).
        void queryClient.cancelQueries({ queryKey: planningKeys.capacityBoard(message.date), exact: true });
        queryClient.setQueryData<CapacityBoardData>(planningKeys.capacityBoard(message.date), (board) => (board ? withTripNow(board, message.tripId, message.trip) : board));
        break;
      case "cap4":
        setCap((previous) => ({ tripId: message.tripId, focus: message.focus, tick: (previous?.tick ?? 0) + 1 }));
        break;
      case "capDragging":
        setCapDrag(message.drag);
        break;
      case "step":
        setTracking(null);
        setPlanStep(message.step);
        break;
      case "plan":
        setBoardPlan((current) => ({
          mode: message.mode ?? current?.mode ?? "full",
          tripId: message.tripId !== undefined ? message.tripId : (current?.tripId ?? null),
          legId: message.legId !== undefined ? message.legId : (current?.legId ?? null),
          radiusKm: message.radiusKm ?? current?.radiusKm ?? NEXT_RADIUS_KM,
          // Left out by a board of a build before open ends could stand together on the map, and by a map window: as it was.
          tripIds: message.tripIds ?? current?.tripIds ?? [],
        }));
        break;
      case "plan2": {
        const { t: _t, ...state } = message;
        setBoardPlan2(state);
        break;
      }
      case "plan3":
        // A click on another map window is no state of the board.
        if (message.clicked) break;
        setBoardPlan3({
          tripId: message.tripId,
          ...(message.tripIds !== undefined ? { tripIds: message.tripIds } : {}),
          ...(message.stage !== undefined ? { stage: message.stage } : {}),
        });
        break;
      case "arm":
        setArmTick((tick) => tick + 1);
        break;
      case "ping":
        post({ t: "hello" });
        break;
      case "close":
        window.close();
        break;
      case "focus":
        // "Kaart los" on the board while this window is open: this one, not a second.
        window.focus();
        break;
      default:
        break;
    }
  });

  // No longer the active window: when the board is what became active, the planner came from here,
  // and the board does not bring this window forward over itself (BoardScreen.tsx).
  useEffect(() => {
    const blurred = () => post({ t: "blurred" });
    window.addEventListener("blur", blurred);
    return () => window.removeEventListener("blur", blurred);
  }, [post]);

  // Here, and gone: the board takes the map back when the window closes, and the window
  // remembers where it stood for the next time.
  useEffect(() => {
    post({ t: "hello" });
    const leave = () => {
      rememberMapWindowPlace();
      post({ t: "bye" });
    };
    window.addEventListener("pagehide", leave);
    return () => window.removeEventListener("pagehide", leave);
  }, [post]);

  // De dag of Plannen, compact in the header (Planning v3, §4.0).
  const planDay = useQuery({
    queryKey: planningKeys.day(date),
    queryFn: () => getPlanningDay(date),
    enabled: canSee,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const map = useQuery({
    // On Plannen the map is the day being planned; step 4 has no map.
    queryKey: planningKeys.map(date, branch || undefined),
    queryFn: () => getPlanningMap(date, branch || undefined),
    enabled: canSee && planStep !== 4,
    // Planning is shared work, and this window may stand alone.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // The lorries on their own rhythm, as on the board itself.
  const fleet = useQuery({
    queryKey: planningKeys.vehicles(date, branch || undefined),
    queryFn: () => getPlanningVehicles(date, branch || undefined),
    enabled: planStep !== 4,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const branches = useQuery({
    queryKey: branchKeys.list,
    queryFn: listBranches,
    staleTime: 5 * 60_000,
    enabled: canSee,
  });
  const branchName = branch ? (branches.data?.find((entry) => entry.code === branch)?.name ?? branch) : t("planning.allBranches");

  // The window's own name in the taskbar.
  useEffect(() => {
    document.title = tracking
      ? t("tracking.mapWindowTitle")
      : t(planStep === 4 ? "planning.cap.windowTitle" : "planning.mapWindowTitle", { date: formatTripDate(date) });
  }, [t, date, tracking, planStep]);

  if (!canSee) {
    return (
      <div style={{ padding: "var(--space-8)", textAlign: "center" }}>
        <p className="t-body-sm t-muted">{t("planning.mapNoAccess")}</p>
      </div>
    );
  }

  if (tracking) {
    // Ritopvolging on the planning's own map (P60): every lorry, the filter, and the lorry chosen with its day.
    return (
      <div
        data-screen-label="Back-office Trip Follow-up Map Window"
        style={{ height: "100vh", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "var(--space-2)", padding: "var(--space-2) var(--space-3) var(--space-3)", background: "var(--surface-canvas)" }}
      >
        <div className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "baseline" }}>
          <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
            {t("nav.tracking")}
          </span>
          <span className="t-body-sm t-muted">{formatTripDate(todayIso())}</span>
          <span className="t-caption t-muted">{t("tracking.mapFollows")}</span>
        </div>
        <div className="wl-card" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <TrackingMapView
            chosen={tracking.choice}
            stage={tracking.stage}
            onPick={(choice) => {
              setTracking((current) => (current ? { ...current, choice } : current));
              post({ t: "trackingPick", vehicleId: choice?.vehicleId ?? null, tripId: choice?.tripId ?? null });
            }}
            onStage={(stage) => {
              setTracking((current) => (current ? { ...current, stage } : current));
              post({ t: "trackingStage", stage });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      data-screen-label="Back-office Planning Map Window"
      style={{
        height: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3) var(--space-3)",
        background: "var(--surface-canvas)",
      }}
    >
      <div className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "center" }}>
        <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
          {formatTripDate(date)}
        </span>
        <span className="t-body-sm t-muted">{branchName}</span>
        {/* The steps of Plannen as pills: a click here picks the step on the board too. */}
        <StepBar day={planDay.data} step={planStep} onStep={(step) => post({ t: "step", step })} pills />
        <span className="t-caption t-muted">{t("planning.mapFollowsBoard")}</span>
        <span className="wl-spacer" />
        {planStep !== 4 && map.isFetching && <span className="t-caption t-muted">{t("common.loading")}</span>}
        {planStep !== 4 && <DayCost totals={planDay.data?.totals} compact />}
      </div>
      {planStep !== 4 && map.isError && (
        <div className="wl-alert wl-alert--danger" role="alert">
          <span>{map.error instanceof Error ? map.error.message : t("common.errorGeneric")}</span>
        </div>
      )}
      {planStep === 4 && <CapacityWindow date={date} canPlan={canPlan} post={post} cap={cap} drag={capDrag} />}
      {/* The map as the step has it (Planning v3, §4.0), and under it the sheet of the trip open on the board (P104, C3):
          while the map stands here, its sheet does too. */}
      {planStep !== 4 && <MapWithTripSheet
        date={date}
        step={planStep}
        board2={boardPlan2}
        board3={boardPlan3}
        day={planDay.data}
        canPlan={canPlan}
        post={post}
        map={
          <PlanMapPane
            date={date}
            data={map.data}
            vehicles={fleet.data?.vehicles}
            fitKey={`${date}|${branch}|plan`}
            step={planStep}
            board={boardPlan}
            board2={boardPlan2}
            board3={boardPlan3}
            day={planDay.data}
            armSignal={armTick}
            post={post}
            canPlan={canPlan}
          />
        }
      />}
    </div>
  );
}
